/**
 * ClassCard - Card component for displaying a single class in grid layout
 *
 * Dashboard3 style glass morphism card
 *
 * @example
 * ```tsx
 * <ClassCard class={classData} onClick={() => router.push(`/classes/${slug}`)} />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { useCallback } from "react";
import { MdAccessTime, MdLocationOn, MdSchool } from "react-icons/md";
import type { GetClassResponse } from "@/openapi/generated";
import { formatTimeTo12Hour } from "@/lib/utils";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Button, Card, Text } from "@/components/ui";
import { ClassRegisterCta } from "./ClassRegisterCta";

export interface ClassCardProps {
  class: GetClassResponse;
  onClick?: () => void;
}

export const ClassCard = ({ class: classData, onClick }: ClassCardProps) => {
  const router = useRouter();
  const theme = useThemeTokens();

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else if (classData.slug) {
      router.push(`/classes/${classData.slug}`);
    }
  }, [onClick, classData.slug, router]);

  const firstDay = classData.classDays?.[0];
  const scheduleText = firstDay
    ? `${firstDay.day} • ${formatTimeTo12Hour(firstDay.startTime)} – ${formatTimeTo12Hour(firstDay.endTime)}`
    : "Schedule TBD";

  const locationText = classData.location?.name || "Location TBD";
  const studioText = classData.classRoom?.name;

  const classId = Number(classData.classId);
  const showRegister = classData.allowOnlineRegistration !== false;

  return (
    <Card
      cursor="pointer"
      transition="all 0.2s ease"
      onClick={handleClick}
      h="100%"
      display="flex"
      flexDirection="column"
      sx={{
        // Hover only on devices that support hover
        "@media (hover: hover)": {
          "&:hover": {
            borderColor: theme.accent.primaryBorder,
            boxShadow: theme.shadow.cardFeatured,
            transform: "translateY(-2px)",
          },
        },
        // Active state for touch devices
        "@media (hover: none) and (pointer: coarse)": {
          "&:active": {
            borderColor: theme.accent.primaryBorder,
            bg: theme.accent.primaryBg,
            transform: "scale(0.98)",
          },
        },
      }}
    >
      <Flex direction="column" p={{ base: 4, md: 5 }} flex={1} gap={3}>
        {/* Header */}
        <Flex direction="column" gap={1} mb={2}>
          <Text
            variant="primary"
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="600"
            letterSpacing="-0.02em"
            noOfLines={2}
            lineHeight="1.3"
          >
            {classData.name}
          </Text>
          {classData.description && (
            <Text
              variant="secondary"
              fontSize="sm"
              noOfLines={2}
              lineHeight="1.5"
              mt={1}
            >
              {classData.description}
            </Text>
          )}
        </Flex>

        {/* Details */}
        <Flex direction="column" gap={2.5} mt="auto">
          {/* Schedule */}
          <Flex align="center" gap={2}>
            <Box color={theme.accent.primary} fontSize="16px">
              <MdAccessTime />
            </Box>
            <Text variant="secondary" fontSize="sm" noOfLines={1}>
              {scheduleText}
            </Text>
          </Flex>

          {/* Location */}
          <Flex align="center" gap={2}>
            <Box color={theme.accent.primary} fontSize="16px">
              <MdLocationOn />
            </Box>
            <Text variant="secondary" fontSize="sm" noOfLines={1}>
              {locationText}
            </Text>
          </Flex>

          {/* Classroom */}
          {studioText && (
            <Flex align="center" gap={2}>
              <Box color={theme.accent.primary} fontSize="16px">
                <MdSchool />
              </Box>
              <Text variant="secondary" fontSize="sm" noOfLines={1}>
                {studioText}
              </Text>
            </Flex>
          )}

          {/* Additional days indicator */}
          {classData.classDays && classData.classDays.length > 1 && (
            <Box mt={1}>
              <Text variant="tertiary" fontSize="xs">
                +{classData.classDays.length - 1} more day{classData.classDays.length - 1 > 1 ? "s" : ""}
              </Text>
            </Box>
          )}
        </Flex>

        <Flex
          gap={2}
          mt={3}
          pt={3}
          borderTopWidth="1px"
          borderColor={theme.borderGlass.default}
          onClick={(e) => e.stopPropagation()}
        >
          {showRegister && (
            <ClassRegisterCta
              classId={classId}
              allowOnlineRegistration={classData.allowOnlineRegistration}
              variant="compact"
            />
          )}
          <Button
            variant="outline"
            size="sm"
            flex={showRegister ? undefined : 1}
            w={showRegister ? undefined : "100%"}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            fontSize="sm"
          >
            View
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};

